// ============================================================================
// AERIUM Clean - Main Application
// ============================================================================

import { router } from 'app/router/router.js';
import { auth } from 'app/auth/auth.js';
import { state } from 'app/state/state.js';
import { sidebar } from 'app/shell/sidebar.js';
import { connectionStatus } from 'app/shell/connection-status.js';
import { staleChunkGuard } from 'app/shell/stale-chunk-guard.js';
import { theme } from 'shared/services/theme.js';
import { zoom } from 'shared/utils/zoom.js';
import { notificationSubscriptionService } from 'shared/services/notification-subscription-service.js';
import { shellUpdateRequired } from 'shared/utils/desktop-shell.js';
import { showToast } from 'shared/toast/toast.js';
import { escapeHtml } from 'shared/utils/dom-helpers.js';

const LOGIN_URL = '/src/features/login/login.html'; // absolute: see AUTH_ROUTES in auth.js

class App {
    constructor() {
        auth.onAuthChange((event) => {
            if (event === 'PASSWORD_RECOVERY') {
                window.location.href = LOGIN_URL;
            }
        });
    }

    async init() {
        try {
            // Arm the stale-build guard before anything that can lazy-import
            // (router pages, on-click modals) so a post-deploy session recovers
            // with a reload instead of dead action buttons.
            staleChunkGuard.init();
            theme.init();
            // Restore the user's chosen zoom and enable browser-style zoom
            // gestures (Ctrl +/-/0 and Ctrl+wheel), which the Pake desktop
            // shell does not provide on its own.
            zoom.init();

            const isAuthenticated = await auth.check();
            if (!isAuthenticated) {
                window.location.href = LOGIN_URL;
                return;
            }

            auth.startSessionMonitor();

            // Do NOT gate first paint on either of these. The sidebar renders its
            // shell synchronously (only its pinned-list fetch is async), and the
            // shared dropdown/state stores are needed app-wide but the dashboard
            // landing page uses none of them. So warm both in the background and
            // let router.init() start navigating immediately. The router awaits
            // state.init() itself before any dropdown-dependent route renders
            // (state.init() is in-flight-guarded, so the duplicate await is free).
            sidebar.load().catch(err => console.error('[App] Sidebar load failed:', err));
            state.init().catch(err => console.error('[App] State init failed:', err));
            connectionStatus.init();
            router.init();
            notificationSubscriptionService.init();

            // Desktop shell only: if the installed Pake binary is missing a
            // capability this build of the web app relies on, nag (10s) to
            // reinstall from the latest MSI. Never fires in a browser, and
            // stops firing the moment the machine runs a current shell.
            if (shellUpdateRequired()) {
                showToast(
                    'The Aerium desktop app needs a one-off reinstall to get the latest '
                    + 'shell update - grab the new installer when you can. This is rare: '
                    + 'most updates apply automatically without a reinstall.',
                    'warning',
                    10000
                );
            }

            // DEV-ONLY drift guard: warn if the data holds a billing_status value
            // that no filter option covers (i.e. the BILLING_REPORT_STATUS enum
            // has fallen behind the DB). Opt-in via localStorage; lazily imported
            // so it never loads for normal users. Flag literal kept in sync with
            // billing-status-drift-check.js (BILLING_STATUS_DRIFT_FLAG).
            if (localStorage.getItem('billingStatusDriftCheck') === 'on') {
                import('features/billing/utils/billing-status-drift-check.js')
                    .then(m => m.checkBillingStatusFilterDrift())
                    .catch(err => console.warn('[billingStatusDrift] check failed:', err?.message || err));
            }

            this.initialized = true;

            // Fallback: redirect recovery tokens to login
            if (window.location.hash?.includes('type=recovery')) {
                window.location.href = LOGIN_URL + window.location.hash;
            }
        } catch (error) {
            console.error('[App] Initialization failed:', error);
            this.showError(error.message);
        }
    }

    showError(message) {
        document.getElementById('app').innerHTML = `
            <div class="app__error">
                <i class="fas fa-exclamation-triangle app__error-icon"></i>
                <h2 class="app__error-title">Initialization Error</h2>
                <p class="app__error-message">${escapeHtml(message)}</p>
                <button onclick="location.reload()" class="dashboard__button dashboard__button--primary">
                    <i class="fas fa-redo dashboard__button-icon"></i> Retry
                </button>
            </div>
        `;
    }
}

const app = new App();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => app.init());
} else {
    app.init();
}

window.app = app;
